import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';

import type { HeaderButtonProps } from 'src/components/layout/redesign/header-mobile/header-button';
import {
  idForMenu,
  mapHeaderContentToSubMenus,
} from 'src/components/layout/redesign/header-mobile/header-mobile-helper';
import HeaderMobileSubMenu from 'src/components/layout/redesign/header-mobile/header-sub-menu';
import { usePageScrollLock } from 'src/custom-hooks/use-page-scroll-lock';

import * as styles from './header-menu.module.scss';

export type BaseHeaderContent = {
  headerMenuItems?: Queries.HeaderLevelOneMenuItem[];
  headerSecondaryUpperMenu?: Queries.HeaderSecondaryUpperMenuItem[];
  headerOtherText?: Queries.HeaderOtherText;
  linkDropdownCta?: Queries.LinkDropdownCta;
};

export type HeaderMobileMenuProps = PropsWithChildren<{
  headerContent: BaseHeaderContent | null;
  primaryButton?: HeaderButtonProps;
  secondaryButton?: HeaderButtonProps;
  searchButton?: HeaderButtonProps;
  isVisible: boolean;
  level1SelectedIndex: number | null;
  level2SelectedIndex: number | null;
  onSelectLevel1Index: (index: number) => void;
  onSelectLevel2Index: (index: number) => void;
  onFinish?: () => void;
}>;

const HeaderMobileMenu = (props: HeaderMobileMenuProps) => {
  usePageScrollLock(props.isVisible && !!props.headerContent);

  const subMenus = useMemo(
    () =>
      mapHeaderContentToSubMenus(
        props.headerContent,
        props.onSelectLevel1Index,
        props.onSelectLevel2Index
      ),
    [props.headerContent, props.onSelectLevel1Index, props.onSelectLevel2Index]
  );

  const currentVisibleId = idForMenu(props.level1SelectedIndex, props.level2SelectedIndex);

  const isChildMenuVisible = (menuId: string) => {
    return currentVisibleId !== menuId && currentVisibleId.startsWith(menuId);
  };

  if (!props.headerContent) {
    return null;
  }

  return (
    <div className={classNames(styles.container, props.isVisible ? styles.visible : styles.hidden)}>
      {subMenus?.map((subMenuProps) => (
        <HeaderMobileSubMenu
          key={subMenuProps.id}
          {...subMenuProps}
          isMenuOpen={props.isVisible}
          isVisible={props.isVisible && currentVisibleId === subMenuProps.id}
          isChildMenuVisible={props.isVisible && isChildMenuVisible(subMenuProps.id)}
          onFinish={props.onFinish}
          headerContent={props.headerContent!}
          primaryButton={props.primaryButton}
          secondaryButton={props.secondaryButton}
          searchButton={props.searchButton}
          linkDropdownCta={props.headerContent?.linkDropdownCta}
        />
      ))}
      {props.children}
    </div>
  );
};
export default HeaderMobileMenu;
